<template>
    <div class="header-section">
        <h1 class="form-title">{{ isEditing ? "Edit Staff" : "Setup Staff" }}</h1>
        <p class="form-subtitle">
            {{ isEditing ? "Update the selected staff record." : "Create a new staff record." }}
        </p>
    </div>

    <div class="form-container">
       
        <p>Fields marked(*) are mandatory</p>

        <div>
            <div class="form-grid">
                <div class="form-group">
                    <label>Full Name <span>*</span></label>
                    <input placeholder="Enter full name" v-model="fullName" type="text" required />
                </div>

                <div class="form-group">
                    <label>Phone <span>*</span></label>
                    <input placeholder="Enter phone number" v-model="phoneNumber" type="text" required />
                </div>

                <div class="form-group">
                    <label>Email <span>*</span></label>
                    <input placeholder="Enter email address" v-model="emailAddress" type="text" required/>
                </div>

                <div class="form-group">
                    <label>Gender <span>*</span></label>
                    <select v-model="gender" required>
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                    </select>
                </div>

                <div class="form-group">
                    <label>Residential Address <span>*</span></label>
                    <textarea
                        cols="30"
                        rows="2"
                        placeholder="Enter residential address"
                        v-model="residentialAddress"
                        required
                    ></textarea>
                </div>
            </div>

            <div class="form-actions">
                <button class="register-btn secondary" type="button" v-if="isEditing" @click="cancelEdit">
                    Cancel Edit
                </button>
                <button class="register-btn" @click="submitForm" v-if="!loading">
                    {{ isEditing ? "Update User" : "Submit Form" }}
                </button>
                <button class="register-btn" type="button" v-if="loading">
                    <i class="fas fa-spinner fa-spin"></i>
                </button>
            </div>
        </div>
    </div>

    <div class="form-container list-section">
        <h2>Saved Users</h2>
        <p>All staff records added from this page appear below.</p>

        <div class="table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Full Name</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Gender</th>
                        <th>Address</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedUsers.length === 0">
                        <td colspan="7" class="no-data">
                            {{ loadingList ? "Loading users..." : "No users available" }}
                        </td>
                    </tr>
                    <tr v-else v-for="(user, index) in paginatedUsers" :key="getUserId(user) || index">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ formatFullName(user) }}</td>
                        <td>{{ user.phoneNumber || user.phone || "-" }}</td>
                        <td>{{ user.emailAddress || user.email || "-" }}</td>
                        <td>{{ user.gender || "-" }}</td>
                        <td>{{ user.residentialAddress || user.address || "-" }}</td>
                        <td class="actions">
                            <button class="btn edit" type="button" @click="editUser(user)" title="Edit user">
                                <PenLine class="icon" />
                            </button>
                            <button class="btn delete" type="button" @click="deleteUser(user)" title="Delete user">
                                <Trash class="icon" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="pagination" v-if="totalPages > 1">
            <button @click="prevPage" :disabled="currentPage === 1">
                <ChevronLeft />
            </button>
            <button
                v-for="page in totalPages"
                :key="page"
                @click="goToPage(page)"
                :class="{ active: currentPage === page }"
            >
                {{ page }}
            </button>
            <button @click="nextPage" :disabled="currentPage === totalPages">
                <ChevronRight />
            </button>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ChevronLeft, ChevronRight, PenLine, Trash } from "lucide-vue-next";
import { computed, onMounted, ref } from "vue";
import axios from "axios";

const fullName = ref("");
const phoneNumber = ref("");
const emailAddress = ref("");
const residentialAddress = ref("");
const gender = ref("");

const users = ref([]);
const loading = ref(false);
const loadingList = ref(false);
const editingUserId = ref(null);
const currentPage = ref(1);
const pageSize = 8;

const isEditing = computed(() => editingUserId.value !== null);
const totalPages = computed(() => Math.ceil(users.value.length / pageSize));
const paginatedUsers = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return users.value.slice(start, start + pageSize);
});

const getUserId = (user) => user?.id ?? user?.user_id ?? user?.userid ?? user?.userId ?? null;

const formatFullName = (user) => {
    if (user?.fullName) return user.fullName;
    if (user?.full_name) return user.full_name;
    return [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "-";
};

const clearForm = () => {
    fullName.value = "";
    phoneNumber.value = "";
    emailAddress.value = "";
    residentialAddress.value = "";
    gender.value = "";
    editingUserId.value = null;
};

const loadUsers = async () => {
    loadingList.value = true;

    const endpoints = [
        "/subadmin_list.php"
    ];

    try {
        for (const endpoint of endpoints) {
            try {
                const response = await axios.get(endpoint);
                if (response.data?.status) {
                    users.value = response.data.data || [];
                    currentPage.value = 1;
                    return;
                }
            } catch (error) {
                continue;
            }
        }

        users.value = [];
        console.error("Unable to load users from any known endpoint");
    } catch (error) {
        console.error(error);
        users.value = [];
    } finally {
        loadingList.value = false;
    }
};

const submitForm = async () => {
    const requiredFields = {
        fullName: fullName.value,
        phoneNumber: phoneNumber.value,
        residentialAddress: residentialAddress.value
    };

    for (const [key, value] of Object.entries(requiredFields)) {
        if (!value || value.trim() === "") {
            alert(`Please fill in the required field: ${key}`);
            return;
        }
    }

    const payload = {
        fullName: fullName.value,
        phoneNumber: phoneNumber.value,
        emailAddress: emailAddress.value,
        residentialAddress: residentialAddress.value,
        gender: gender.value
    };

    if (editingUserId.value !== null) {
        payload.id = editingUserId.value;
        payload.user_id = editingUserId.value;
    }

    try {
        loading.value = true;

        const endpoint = editingUserId.value !== null ? "/subadmin_update.php" : "/subadmin_create.php";
        const response = await axios.post(endpoint, payload, {
            headers: {
                "Content-Type": "application/json"
            }
        });

        alert("Success: " + (response.data?.message || "Saved successfully"));
        clearForm();
        await loadUsers();
    } catch (error) {
        console.error(error);
        alert(error.response?.data?.message || error.message);
    } finally {
        loading.value = false;
    }
};

const editUser = (user) => {
    editingUserId.value = getUserId(user);
    fullName.value = user.fullName || user.full_name || formatFullName(user);
    phoneNumber.value = user.phoneNumber || user.phone || "";
    emailAddress.value = user.emailAddress || user.email || "";
    residentialAddress.value = user.residentialAddress || user.address || "";
    gender.value = user.gender || "";
    window.scrollTo({ top: 0, behavior: "smooth" });
};

const deleteUser = async (user) => {
    const userId = getUserId(user);
    if (!userId) {
        alert("Unable to delete this user because the record id is missing.");
        return;
    }

    if (!confirm(`Are you sure you want to delete ${formatFullName(user)}?`)) {
        return;
    }

    try {
        const response = await axios.get(`/subadmin_delete.php?id=${userId}`);
        if (response.data?.status) {
            alert(response.data.message || "User deleted successfully");
            users.value = users.value.filter((item) => getUserId(item) !== userId);
            currentPage.value = Math.min(currentPage.value, Math.max(totalPages.value, 1));
            if (editingUserId.value === userId) {
                clearForm();
            }
        } else {
            alert(response.data?.message || "Unable to delete user");
        }
    } catch (error) {
        console.error(error);
        alert(error.response?.data?.message || error.message);
    }
};

const cancelEdit = () => {
    clearForm();
};

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
}

function prevPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
}

onMounted(() => {
    loadUsers();
});
</script>

<style scoped lang="scss">
.form-container {
    max-width: 90%;
    margin: 0 auto;
    padding: 24px;

    h2 {
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 8px;
        text-align: left;
    }

    p {
        color: #666;
        margin-bottom: 24px;
        text-align: left;
    }

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 20px;
    }
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    label {
        font-size: 15px;
        font-weight: 500;
        margin-bottom: 6px;
        color: #30373d;
    }

    input,
    select,
    textarea {
        padding: 10px 12px;
        border: 1px solid #d1d1d1;
        width: 100%;
        border-radius: 14px;
        font-size: 14px;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.3);
            outline: none;
        }
    }
}

.form-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
}

.register-btn {
    width: 450px;
    max-width: 100%;
    padding: 14px 12px;
    border: 0;
    color: #ffffff;
    background: #ed4e24;
    font-size: 16px;
    font-weight: 600;
    border-radius: 60px;
    cursor: pointer;
    margin-top: 0.2rem;
    transition: background 0.3s ease;
    margin-bottom: 0;
}

.register-btn.secondary {
    background: #ffffff;
    color: #ed4e24;
    border: 1px solid #ed4e24;
}

@media (max-width: 768px) {
    .register-btn {
        width: 100%;
    }
}

.list-section {
    margin-top: 12px;
}

.table-responsive {
    width: 100%;
    overflow-x: auto;
}

.table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow-x: auto;
}

.table thead {
    display: table-header-group;
}

.table tbody {
    display: table-row-group;
}

.table th,
.table td {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    box-shadow: #6c757d33 0px 1px 3px 0px, #6c757d1a 0px 1px 2px 0px;
    padding: 12px 16px;
    text-align: left;
    font-size: 14px;
}

.table th {
    background: #fff;
    color: #6c757d;
}

.actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.btn {
    width: 36px;
    height: 36px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    color: #343a40;
    background: white;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
}

.btn:hover {
    border: 1px solid #0dcaf0;
    color: #0dcaf0;
}

.btn .icon {
    width: 18px;
    height: 18px;
}

.pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
}

.pagination button {
    width: 36px;
    height: 36px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    color: #343a40;
    background: white;
    cursor: pointer;
}

.pagination button.active {
    border: 1px solid #0057fc;
    color: #0057fc;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #e9ecef;
}

.no-data {
    text-align: center;
}

@media (max-width: 768px) {
    .table {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }

    .table th,
    .table td {
        padding: 8px 10px;
        font-size: 12px;
    }
}
</style>
